/**
 * 单所学校的"介绍卡片"——用于报告页 P5–P9 + 也可用于在线 H5 主页。
 *
 * 设计原则（与用户讨论一致）：
 *  - 只展示客观陈述
 *  - 占位字段（"（待补"开头）显示"信息整理中"灰色提示，不显示原始占位文字
 */
import type { SchoolProfile } from '@/domain/schools'
import { isPlaceholder } from '@/domain/schools'

export function SchoolProfileCard({ profile, compact = false }: { profile: SchoolProfile; compact?: boolean }) {
  return (
    <section className="card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-h2 text-ink-900">{profile.name}</h3>
          {!isPlaceholder(profile.full_name) && (
            <div className="text-ink-500 text-sm mt-0.5">{profile.full_name}</div>
          )}
        </div>
        {!isPlaceholder(profile.level) && (
          <span className="tag tag-recommended">{profile.level}</span>
        )}
      </div>

      {/* 主图（如果有） */}
      <div className="mt-4">
        <ImageOrPlaceholder src={profile.image_main} alt={`${profile.name} 校园`} aspect="16/10" />
      </div>

      {/* 关键事实 */}
      <div className="mt-5 grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
        <Field label="建校年份" value={profile.founded} />
        <Field label="寄宿情况" value={profile.boarding} />
        <Field label="校址" value={profile.address} fullWidth />
        <Field label="在校规模" value={profile.scale} />
        <Field label="2025 招生" value={profile.enrollment_2025.note} fullWidth />
      </div>

      {/* 办学特色 */}
      {profile.features.some((f) => !isPlaceholder(f)) && (
        <div className="mt-5 pt-4 border-t border-ink-100">
          <div className="text-ink-500 text-sm mb-2">办学特色</div>
          <ul className="space-y-1.5 list-disc pl-5 text-ink-800">
            {profile.features.filter((f) => !isPlaceholder(f)).map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      {/* 占位提示 */}
      {!compact && hasAnyPlaceholder(profile) && (
        <div className="mt-5 pt-4 border-t border-ink-100 text-xs text-ink-400 leading-relaxed">
          部分信息正在整理中，完整版将于近期同步更新。
          以学校官方公告为准。
        </div>
      )}

      {/* 地图 */}
      <div className="mt-5">
        <div className="text-ink-500 text-sm mb-2">校址位置</div>
        <ImageOrPlaceholder src={profile.image_map} alt={`${profile.name} 位置图`} aspect="4/3" />
      </div>
    </section>
  )
}

function hasAnyPlaceholder(p: SchoolProfile): boolean {
  const fields = [p.full_name, p.founded, p.address, p.level, p.boarding, p.scale, p.enrollment_2025.note]
  return fields.some(isPlaceholder) || p.features.every(isPlaceholder)
}

function Field({ label, value, fullWidth }: { label: string; value: string; fullWidth?: boolean }) {
  const placeholder = isPlaceholder(value)
  return (
    <div className={fullWidth ? 'col-span-2' : ''}>
      <div className="text-ink-500 text-xs mb-0.5">{label}</div>
      <div className={placeholder ? 'text-ink-300 italic' : 'text-ink-800'}>
        {placeholder ? '信息整理中' : value}
      </div>
    </div>
  )
}

function ImageOrPlaceholder({ src, alt, aspect }: { src: string; alt: string; aspect: string }) {
  return (
    <div
      className="w-full bg-ink-100 rounded-xl overflow-hidden flex items-center justify-center text-ink-300"
      style={{ aspectRatio: aspect }}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        onError={(e) => {
          // 图片不存在时，显示占位
          const img = e.currentTarget
          img.style.display = 'none'
          if (img.parentElement) img.parentElement.innerHTML = '<span class="text-sm">图片整理中</span>'
        }}
      />
    </div>
  )
}
